/**
 * Minimal Server-Sent Events reader for a `fetch` body (POST bodies cannot use
 * `EventSource`). Yields one `{event, data}` per dispatched event; `data` is
 * the joined `data:` lines, left for the caller to parse. Comments and fields
 * other than `event`/`data` are ignored, as the SSE spec says.
 */
export interface SseEvent {
  event: string;
  data: string;
}

export function createSseParser(onEvent: (event: SseEvent) => void) {
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];

  const dispatch = () => {
    if (dataLines.length > 0) {
      onEvent({ event: eventName || 'message', data: dataLines.join('\n') });
    }
    eventName = '';
    dataLines = [];
  };

  const line = (raw: string) => {
    if (raw === '') {
      dispatch();
      return;
    }
    if (raw.startsWith(':')) return;
    const colon = raw.indexOf(':');
    const field = colon === -1 ? raw : raw.slice(0, colon);
    let value = colon === -1 ? '' : raw.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') eventName = value;
    else if (field === 'data') dataLines.push(value);
  };

  return {
    push(chunk: string) {
      buffer += chunk;
      let index: number;
      while ((index = buffer.search(/\r\n|\r|\n/)) !== -1) {
        const newline = buffer.startsWith('\r\n', index) ? 2 : 1;
        line(buffer.slice(0, index));
        buffer = buffer.slice(index + newline);
      }
    },
    /** End of stream: a trailing event without its blank line still counts. */
    end() {
      if (buffer !== '') line(buffer);
      buffer = '';
      dispatch();
    },
  };
}

export async function readSse(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: SseEvent) => void
): Promise<void> {
  const parser = createSseParser(onEvent);
  const reader = body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parser.push(decoder.decode(value, { stream: true }));
  }
  parser.push(decoder.decode());
  parser.end();
}
